import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

class AppInsightsLogger {
  constructor() {
    this.appInsights = null;
    this.reactPlugin = null;
    this.isInitialized = false;
    this.sessionId = null;
    
    // Auto-initialize with tokenized configuration
    this.autoInitialize();
  }

  /**
   * Auto-initialize Application Insights with tokenized configuration
   */
  autoInitialize() {
    // Use build-time tokenized connection string
    const connectionString = __APP_INSIGHTS_CONNECTION_STRING__;
    
    if (connectionString) {
      this.initialize(connectionString);
    } else {
      console.log('Application Insights: No connection string provided - telemetry disabled');
    }
  }

  /**
   * Initialize Application Insights
   * @param {string} connectionString - Application Insights connection string
   */
  initialize(connectionString) {
    if (this.isInitialized || !connectionString) {
      return;
    }

    try {
      this.reactPlugin = new ReactPlugin();
      
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: connectionString,
          extensions: [this.reactPlugin],
          enableAutoRouteTracking: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          enableCorsCorrelation: true,
          enableUnhandledPromiseRejectionTracking: true,
          enableDebug: process.env.NODE_ENV === 'development',
          loggingLevelConsole: process.env.NODE_ENV === 'development' ? 2 : 0,
          autoTrackPageVisitTime: true,
          enableAjaxErrorStatusText: true,
          extensionConfig: {
            [this.reactPlugin.identifier]: {
              history: null, // Will be set when router history is available
            }
          }
        }
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView(); // Track initial page view
      
      this.isInitialized = true;
      console.log('Application Insights initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  /**
   * Set user context
   * @param {string} userId - User ID
   * @param {string} accountId - Account ID
   * @param {Object} additionalProperties - Additional user properties
   */
  setUser(userId, accountId = null, additionalProperties = {}) {
    if (!this.isInitialized) {
      return;
    }
    
    try {
      this.appInsights.setAuthenticatedUserContext(userId, accountId);
      
      // Set additional user properties
      Object.keys(additionalProperties).forEach(key => {
        this.appInsights.addTelemetryInitializer((envelope) => {
          envelope.tags[`ai.user.${key}`] = additionalProperties[key];
        });
      });
    } catch (error) {
      console.error('Failed to set user context:', error);
    }
  }

  /**
   * Clear user context
   */
  clearUser() {
    if (!this.isInitialized) {
      return;
    }
    
    try {
      this.appInsights.clearAuthenticatedUserContext();
    } catch (error) {
      console.error('Failed to clear user context:', error);
    }
  }

  /**
   * Track custom events
   * @param {string} name - Event name
   * @param {Object} properties - Custom properties
   * @param {Object} measurements - Custom measurements
   */
  trackEvent(name, properties = {}, measurements = {}) {
    if (!this.isInitialized) {
      console.log(`[AppInsights] Event: ${name}`, properties, measurements);
      return;
    }
    
    try {
      this.appInsights.trackEvent({
        name,
        properties: {
          timestamp: new Date().toISOString(),
          sessionId: this.getSessionId(), // Simple session-based correlation
          ...properties
        },
        measurements
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Get or create a session ID for correlation
   * @returns {string} Session ID
   */
  getSessionId() {
    if (!this.sessionId) {
      this.sessionId = `session_${crypto.randomUUID()}`;
    }
    return this.sessionId;
  }

  /**
   * Track exceptions
   * @param {Error} error - Error object
   * @param {Object} properties - Additional properties
   * @param {string} severityLevel - Severity level
   */
  trackException(error, properties = {}, severityLevel = 'Error') {
    if (!this.isInitialized) {
      console.error(`[AppInsights] Exception:`, error, properties);
      return;
    }
    
    try {
      this.appInsights.trackException({
        exception: error,
        properties: {
          timestamp: new Date().toISOString(),
          sessionId: this.getSessionId(), // Include session for correlation
          component: properties.component || 'Unknown',
          ...properties
        },
        severityLevel
      });
    } catch (trackError) {
      console.error('Failed to track exception:', trackError);
    }
  }

  /**
   * Flush all telemetry data
   */
  flush() {
    if (!this.isInitialized) {
      return;
    }
    
    try {
      this.appInsights.flush();
    } catch (error) {
      console.error('Failed to flush telemetry:', error);
    }
  }

  /**
   * Get the React plugin for router integration
   */
  getReactPlugin() {
    return this.reactPlugin;
  }
}

// Create singleton instance and auto-initialize
const appInsightsLogger = new AppInsightsLogger();

export default appInsightsLogger;
export { appInsightsLogger as appInsights };
